import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Menu, MenuItem, Paper, Typography, Tooltip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import * as C from '../../theme/colors.constants';

/**
 * Botão do menu principal da home.
 *
 * Três estados visuais, extraídos dos prints:
 *   - habilitado:  card branco, texto navy bold, sombra suave
 *   - com submenu: mesmo card + chip teal com a seta no canto superior direito
 *   - desabilitado: fundo #b9bdc3 e texto #6b7280, sem sombra e sem o chip
 *
 * Diferença consciente em relação ao projeto real: no abe-web-react o item sem
 * permissão é OCULTADO (`hasNotUserPermissions` → `null`). Nos novos prints o
 * padrão passa a ser DESABILITAR, então é isso que o protótipo faz.
 *
 * `views` chega já filtrada pelo pai (RBAC via `hasUserPermissions` + catálogo
 * de permissões da Home) — este componente não faz nenhuma filtragem própria.
 */
export default function HomeMenuButton({
  label, views, showDropdown, disabled, onSelect,
}) {
  const [anchor, setAnchor] = useState(null);
  const temDropdown = showDropdown && Array.isArray(views) && views.length > 0;

  const abrir = (e) => {
    if (disabled) return;
    if (temDropdown) setAnchor(e.currentTarget);
    else if (onSelect) onSelect(label);
  };

  const card = (
    <Paper
      onClick={abrir}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-haspopup={temDropdown || undefined}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && abrir(e)}
      sx={{
        position: 'relative',
        height: 'clamp(56px, 4.1vw, 92px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: disabled ? C.disabledBg : C.white,
        boxShadow: disabled ? 'none' : C.shadowCard,
        transition: 'box-shadow .15s ease, transform .15s ease',
        userSelect: 'none',
        '&:hover': disabled
          ? undefined
          : { boxShadow: '0 4px 12px rgba(13,43,82,0.16)', transform: 'translateY(-1px)' },
        '&:focus-visible': { outline: `2px solid ${C.navy}`, outlineOffset: 2 },
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 'clamp(14px, 0.92vw, 22px)',
          color: disabled ? C.disabledText : C.navy,
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>

      {temDropdown && !disabled ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 22,
            height: 18,
            borderRadius: '5px',
            backgroundColor: C.tealSoft,
            color: C.teal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ArrowDropDownIcon sx={{ fontSize: 20 }} />
        </Box>
      ) : null}
    </Paper>
  );

  return (
    <>
      {disabled ? (
        <Tooltip title="Você não tem permissão para acessar esta função" arrow placement="top">
          <span style={{ display: 'block' }}>{card}</span>
        </Tooltip>
      ) : (
        card
      )}

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 268,
            borderRadius: 2,
            boxShadow: C.shadowFloat,
            border: `1px solid ${C.borderGrey}`,
          },
        }}
      >
        {(views || []).map(v => (
          <MenuItem
            key={v}
            onClick={() => {
              setAnchor(null);
              if (onSelect) onSelect(v);
            }}
            sx={{ py: 1, color: C.black }}
          >
            {v}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
HomeMenuButton.propTypes = {
  label: PropTypes.string.isRequired,
  views: PropTypes.arrayOf(PropTypes.string),
  showDropdown: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
};
HomeMenuButton.defaultProps = {
  views: [],
  showDropdown: false,
  disabled: false,
  onSelect: null,
};
